/**
 * Реестр брендов для CRM.
 *
 * Система разворачивается экземплярами на клиента: код один, образ один,
 * а различия живут в `.env` экземпляра — переменная `BRAND_PROFILE`. Сайт
 * умел это с самого начала (apps/site-westauto/src/content/brands/), CRM —
 * нет, и клиент видел чужое имя ровно там, где складывается первое
 * впечатление: на экране входа.
 *
 * Реестр лежит в shared, а не в API или в приложении, потому что нужен обоим:
 * API отдаёт запись наружу (`GET /api/brand`), приложение её показывает.
 * Если бы запись жила в API, у приложения не было бы её типа; если бы в
 * приложении — API нечего было бы отдавать.
 *
 * Полей здесь ровно столько, сколько нужно CRM. Контакты, логотипы, палитра
 * сайта и прочее богатство профиля сайта сюда не переносятся: у CRM один
 * брендированный экран и одна шапка, а лишнее поле — это поле, которое
 * однажды разойдётся с сайтом и начнёт врать.
 */

/**
 * Знак бренда в шапке и на экране входа.
 *
 * Строкой, а не компонентом: shared собирается без React и работает в том
 * числе на сервере. Сопоставление строки с картинкой — дело приложения
 * (apps/app/src/components/Icons.tsx).
 */
export type BrandMark = 'key' | 'ship';

export interface CrmBrand {
  /** Значение `BRAND_PROFILE`, по которому профиль выбирается. */
  readonly id: string;

  /** Полное название — для подвала и заголовков. */
  readonly name: string;

  /**
   * Хвост названия, который вёрстка выделяет курсивом (`Авто<em>Ключ</em>`).
   *
   * Храним хвост, а не две половины: половины рано или поздно разойдутся с
   * полным именем, а хвост от него неотделим — он обязан быть его концом,
   * и `splitBrandName()` это проверяет. У односоставного имени хвоста нет —
   * `null`, а не пустая строка: «нет выделения» и «выделить ничего» это
   * разные вещи только на вид, но пустая строка прячет опечатку.
   */
  readonly nameEmphasis: string | null;

  /** Знак рядом с названием. */
  readonly mark: BrandMark;

  /**
   * Логотип картинкой для документов клиенту — путь внутри приложения,
   * относительно его базового адреса (`/app/`). `null` — логотипа нет,
   * документ рисует прежнюю пару «svg-знак + название».
   *
   * Путь относительный намеренно: shared не знает, по какому адресу
   * развёрнуто приложение, а приложение знает (`import.meta.env.BASE_URL`).
   * Абсолютный `/app/...` тихо сломался бы на экземпляре с другим префиксом.
   *
   * Только для клиентских документов. Шапка и экран входа остаются на
   * svg-знаке: он рисуется цветом темы и не мылится на любом размере.
   */
  readonly logo: string | null;

  /**
   * Акцентный цвет (`--accent`) или `null` — «оставить цвет темы».
   *
   * `null` нужен не для красоты: у АвтоКлюча акцент задан в app.css дважды —
   * свой для тёмной темы и свой для светлой. Один hex не может быть обоими,
   * и жёсткое значение здесь испортило бы светлую тему живого экземпляра.
   * Профиль, которому своя палитра не нужна, ставит `null` и получает ровно
   * то, что было до появления реестра.
   */
  readonly accent: string | null;
  /**
   * Цвет текста на акцентных кнопках или `null` — «оставить цвет темы».
   *
   * Отдельно от accent потому, что контраст зависит от самого акцента:
   * на тёплом золотом АвтоКлюча читается тёмный текст, на зелёном — белый.
   * Одним полем это не выражается, а ошибка видна сразу на каждой кнопке.
   */
  readonly accentInk: string | null;

  /** Фон тёмной панели входа (`--ink`) или `null` — «оставить цвет темы». */
  readonly darkPanel: string | null;

  /** Короткая фраза на экране входа — одна мысль, в кавычках-ёлочках. */
  readonly quote: string;

  /** Абзац под фразой: чем система занимается. Два-три предложения. */
  readonly intro: string;

  /**
   * Подпись в подвале экрана входа. `{year}` подставляется текущим годом —
   * см. `brandFooter()`.
   *
   * Строкой целиком, а не «названием плюс примечанием»: у следующего
   * экземпляра подпись может оказаться на другом языке или с другой
   * структурой, и собирать её из кусков тогда нечем.
   */
  readonly footer: string;
}

/**
 * АвтоКлюч / WestAuto — исходный экземпляр, он же поведение по умолчанию.
 *
 * Тексты перенесены с экрана входа дословно. Любая правка здесь меняет вид
 * живого сайта, поэтому править только вместе со сверкой до/после.
 */
export const westautoCrmBrand: CrmBrand = {
  id: 'westauto',
  name: 'АвтоКлюч',
  nameEmphasis: 'Ключ',
  mark: 'key',
  // Логотип WestAuto: внутри картинки уже есть название, поэтому в документе
  // она заменяет собой и знак, и надпись
  logo: 'brand/westauto-logo-256.png',
  accent: null,
  accentInk: null,
  darkPanel: null,
  quote: '«Один расчёт — от ставки на аукционе до цены клиенту.»',
  intro:
    'Платформа расчёта стоимости доставки и растаможки авто с Copart и IAAI под ключ. ' +
    'Тарифы, история изменений и роли — в одном месте.',
  footer: '© {year} АвтоКлюч · доступ только для сотрудников',
};

/**
 * Larus Logistics — экземпляр на laruslogistics.seoshkin.tools.
 *
 * Факты взяты из профиля их сайта (apps/site-westauto/src/content/brands/
 * larus.ts): аукционы, маршрут через Клайпеду, выдача во Львове, работа с
 * 2020 года. Ничего придуманного: выдуманная цифра на экране входа подрывает
 * доверие ко всему, что система потом посчитает.
 *
 * Подпись по-русски, хотя клиент украиноязычный: интерфейс CRM целиком
 * русский, и одна украинская строка посреди него выглядит не заботой,
 * а недоделкой. Переводить CRM — отдельная задача, не эта.
 */
export const larusCrmBrand: CrmBrand = {
  id: 'larus',
  name: 'Larus Logistics',
  nameEmphasis: null,
  mark: 'ship',
  // Логотипа от клиента нет — документы рисуют знак с названием, как раньше
  logo: null,
  accent: '#16a34a',
  accentInk: '#ffffff',
  darkPanel: '#0f1b2d',
  quote: '«Лот на аукционе США — машина во Львове.»',
  intro:
    'Доставка авто с COPART, IAAI и Manheim в Украину под ключ: выкуп лота, море до порта ' +
    'Клайпеда, оформление и выдача во Львове. Работаем с 2020 года.',
  footer: '© {year} Larus Logistics · доступ только для сотрудников',
};

/** Профиль по умолчанию: без `BRAND_PROFILE` экземпляр работает как раньше. */
export const DEFAULT_CRM_BRAND = westautoCrmBrand;

export const CRM_BRANDS: Record<string, CrmBrand> = {
  [westautoCrmBrand.id]: westautoCrmBrand,
  [larusCrmBrand.id]: larusCrmBrand,
};

/**
 * Профиль по значению `BRAND_PROFILE`.
 *
 * Опечатка в `.env` не должна ронять вход в систему: отдаём прежний бренд и
 * пишем предупреждение. Падение здесь означало бы, что из-за лишней буквы в
 * переменной никто не может войти в CRM, — цена ошибки несопоставима.
 */
export function resolveCrmBrand(profile: string | null | undefined): CrmBrand {
  const id = profile?.trim();
  if (!id) return DEFAULT_CRM_BRAND;

  const brand = CRM_BRANDS[id];
  if (!brand) {
    console.warn(`[brand] профиль «${id}» неизвестен — беру ${DEFAULT_CRM_BRAND.id}`);
    return DEFAULT_CRM_BRAND;
  }
  return brand;
}

/**
 * Название, разобранное на обычную часть и выделяемый хвост.
 *
 * Хвост, который не является концом имени, — опечатка в профиле. Молча
 * показывать такое имя нельзя (получится «АвтоКлючКлюч» или обрезок),
 * поэтому выделение в этом случае просто не применяется.
 */
export function splitBrandName(brand: CrmBrand): { lead: string; emphasis: string | null } {
  const { name, nameEmphasis } = brand;
  if (!nameEmphasis || !name.endsWith(nameEmphasis) || name === nameEmphasis) {
    return { lead: name, emphasis: null };
  }
  return { lead: name.slice(0, name.length - nameEmphasis.length), emphasis: nameEmphasis };
}

/** Подпись подвала с подставленным годом. */
export function brandFooter(brand: CrmBrand, year: number): string {
  return brand.footer.replace('{year}', String(year));
}
